import { initialState } from './reducer';
import { APP_OPERATIONS } from './constants';
import type { TReducer, AclOperation } from './types';
import type { RootState } from 'types/rootState';

// ─── Base selectors ──────────────────────────────────────────────────

const stateData = (state: RootState): TReducer => state['user'] || initialState;

export const getUserInfo = (state: RootState): TReducer => stateData(state);
export const getUserEmail = (state: RootState): string => stateData(state)?.userEmail;
export const getUserGrpId = (state: RootState): string => stateData(state)?.grpId;
export const getUserTenentList = (state: RootState) => stateData(state)?.allTenentList;
export const getWebAppSettings = (state: RootState) => stateData(state)?.webAppSettings ?? {};
export const getUserSettings = (state: RootState) => getUserInfo(state).settings;
export const getAclOperations = (state: RootState): AclOperation[] => stateData(state)?.operations;
export const getAclRole = (state: RootState) => stateData(state)?.role;
export const getCurrentWorkspace = (state: RootState) => stateData(state)?.currentWorkspace ?? {};
export const isAccessControlEnabled = (state: RootState) =>
	(getWebAppSettings(state) as Record<string, unknown>)?.IsAclRowLevelAccessEnabled;
export const isNewUploadEnabled = (state: RootState) =>
	(getUserSettings(state) as Record<string, unknown> | null)?.IsNewUploadEnabled;

export const hasAclOperations = (state: RootState): boolean =>
	isAccessControlEnabled(state) ? Boolean(getAclOperations(state)?.length) : true;

const findAccessOperation = (state: RootState, operation: string): boolean => {
	return Boolean(getAclOperations(state)?.find((o: AclOperation) => o.operation === operation));
};

// ─── ACL Selector Factory ────────────────────────────────────────────

type AclSelector = (state: RootState) => boolean;

interface AclSelectors {
	isRead: AclSelector;
	isWrite: AclSelector;
	isDelete: AclSelector;
	isReadWrite: AclSelector;
}

/**
 * Creates a set of Read / Write / Delete / ReadWrite selectors for a given
 * ACL-controlled module.  When `requireAcl` is true the selectors also
 * check that the user has *any* ACL operations assigned (i.e. ACL is active
 * for this tenant).
 */
function createAclSelectors(
	readOp: string,
	writeOp: string,
	deleteOp: string,
	options: { requireAcl?: boolean } = {}
): AclSelectors {
	const guard: AclSelector = options.requireAcl ? (state) => hasAclOperations(state) : () => true;

	const isRead: AclSelector = (state) => guard(state) && findAccessOperation(state, readOp);
	const isWrite: AclSelector = (state) => guard(state) && findAccessOperation(state, writeOp);
	const isDelete: AclSelector = (state) => guard(state) && findAccessOperation(state, deleteOp);
	const isReadWrite: AclSelector = (state) => isRead(state) || isWrite(state);

	return { isRead, isWrite, isDelete, isReadWrite };
}

// ─── Order modules ───────────────────────────────────────────────────

const createOrder = createAclSelectors(
	APP_OPERATIONS.OP_CREATE_ORDER_READ,
	APP_OPERATIONS.OP_CREATE_ORDER_WRITE,
	APP_OPERATIONS.OP_CREATE_ORDER_DELETE
);
export const isCreateOrderRead = createOrder.isRead;
export const isCreateOrderReadWrite = createOrder.isReadWrite;

const availability = createAclSelectors(
	APP_OPERATIONS.OP_AVAILABILITY_READ,
	APP_OPERATIONS.OP_AVAILABILITY_WRITE,
	APP_OPERATIONS.OP_AVAILABILITY_DELETE
);
export const isAvailabilityRead = availability.isRead;
export const isAvailabilityWrite = availability.isWrite;
export const isAvailabilityDelete = availability.isDelete;
export const isAvailabilityReadWrite = availability.isReadWrite;

const availabilitySummary = createAclSelectors(
	APP_OPERATIONS.OP_AVAILABILITY_SUMMARY_READ,
	APP_OPERATIONS.OP_AVAILABILITY_SUMMARY_WRITE,
	APP_OPERATIONS.OP_AVAILABILITY_SUMMARY_DELETE
);
export const isAvailabilitySummaryRead = availabilitySummary.isRead;
export const isAvailabilitySummaryWrite = availabilitySummary.isWrite;
export const isAvailabilitySummaryDelete = availabilitySummary.isDelete;
export const isAvailabilitySummaryReadWrite = availabilitySummary.isReadWrite;

const allOrders = createAclSelectors(
	APP_OPERATIONS.OP_ALL_ORDERS_READ,
	APP_OPERATIONS.OP_ALL_ORDERS_WRITE,
	APP_OPERATIONS.OP_ALL_ORDERS_DELETE
);
export const isAllOrdersRead = allOrders.isRead;
export const isAllOrdersWrite = allOrders.isWrite;
export const isAllOrdersDelete = allOrders.isDelete;
export const isAllOrdersReadWrite = allOrders.isReadWrite;

const orderItems = createAclSelectors(
	APP_OPERATIONS.OP_ORDER_ITEMS_READ,
	APP_OPERATIONS.OP_ORDER_ITEMS_WRITE,
	APP_OPERATIONS.OP_ORDER_ITEMS_DELETE
);
export const isOrderItemsRead = orderItems.isRead;
export const isOrderItemsWrite = orderItems.isWrite;
export const isOrderItemsDelete = orderItems.isDelete;
export const isOrderItemsReadWrite = orderItems.isReadWrite;

const orderSkus = createAclSelectors(
	APP_OPERATIONS.OP_ORDER_SKUS_READ,
	APP_OPERATIONS.OP_ORDER_SKUS_WRITE,
	APP_OPERATIONS.OP_ORDER_SKUS_DELETE
);
export const isOrderSkusRead = orderSkus.isRead;
export const isOrderSkusWrite = orderSkus.isWrite;
export const isOrderSkusDelete = orderSkus.isDelete;
export const isOrderSkusReadWrite = orderSkus.isReadWrite;

const orderKits = createAclSelectors(
	APP_OPERATIONS.OP_ORDER_KITS_READ,
	APP_OPERATIONS.OP_ORDER_KITS_WRITE,
	APP_OPERATIONS.OP_ORDER_KITS_DELETE
);
export const isOrderKitsRead = orderKits.isRead;
export const isOrderKitsWrite = orderKits.isWrite;
export const isOrderKitsDelete = orderKits.isDelete;
export const isOrderKitsReadWrite = orderKits.isReadWrite;

// TODO: Previously hardcoded to `true` — now checks actual ACL permissions.
// Verify with product team that Order Pivot access is correctly configured for all tenants.
const orderPivot = createAclSelectors(
	APP_OPERATIONS.OP_ORDER_PIVOT_READ,
	APP_OPERATIONS.OP_ORDER_PIVOT_WRITE,
	APP_OPERATIONS.OP_ORDER_PIVOT_DELETE
);
export const isOrderPivotRead = orderPivot.isRead;
export const isOrderPivotWrite = orderPivot.isWrite;
export const isOrderPivotDelete = orderPivot.isDelete;
export const isOrderPivotReadWrite = orderPivot.isReadWrite;

const advertising = createAclSelectors(
	APP_OPERATIONS.OP_ADVERTISING_READ,
	APP_OPERATIONS.OP_ADVERTISING_WRITE,
	APP_OPERATIONS.OP_ADVERTISING_DELETE
);
export const isAdvertisingRead = advertising.isRead;
export const isAdvertisingWrite = advertising.isWrite;
export const isAdvertisingDelete = advertising.isDelete;
export const isAdvertisingReadWrite = advertising.isReadWrite;

const parameters = createAclSelectors(
	APP_OPERATIONS.OP_PARAMETERS_READ,
	APP_OPERATIONS.OP_PARAMETERS_WRITE,
	APP_OPERATIONS.OP_PARAMETERS_DELETE
);
export const isParametersRead = parameters.isRead;
export const isParametersWrite = parameters.isWrite;
export const isParametersDelete = parameters.isDelete;
export const isParametersReadWrite = parameters.isReadWrite;

// ─── Demand modules ──────────────────────────────────────────────────

const demandChange = createAclSelectors(
	APP_OPERATIONS.OP_DEMAND_CHANGE_READ,
	APP_OPERATIONS.OP_DEMAND_CHANGE_WRITE,
	APP_OPERATIONS.OP_DEMAND_CHANGE_DELETE
);
export const isDemandChangeRead = demandChange.isRead;
export const isDemandChangeWrite = demandChange.isWrite;
export const isDemandChangeDelete = demandChange.isDelete;
export const isDemandChangeReadWrite = demandChange.isReadWrite;

const demandReporting = createAclSelectors(
	APP_OPERATIONS.OP_DEMAND_REPORTING_READ,
	APP_OPERATIONS.OP_DEMAND_REPORTING_WRITE,
	APP_OPERATIONS.OP_DEMAND_REPORTING_DELETE
);
export const isDemandReportingRead = demandReporting.isRead;
export const isDemandReportingWrite = demandReporting.isWrite;
export const isDemandReportingDelete = demandReporting.isDelete;
export const isDemandReportingReadWrite = demandReporting.isReadWrite;

const demandGroupHub = createAclSelectors(
	APP_OPERATIONS.OP_DEMAND_GROUP_HUB_READ,
	APP_OPERATIONS.OP_DEMAND_GROUP_HUB_WRITE,
	APP_OPERATIONS.OP_DEMAND_GROUP_HUB_DELETE
);
export const isDemandGroupHubRead = demandGroupHub.isRead;
export const isDemandGroupHubWrite = demandGroupHub.isWrite;
export const isDemandGroupHubDelete = demandGroupHub.isDelete;
export const isDemandGroupHubReadWrite = demandGroupHub.isReadWrite;

export const isIntelligenceRead = (state: RootState): boolean =>
	findAccessOperation(state, APP_OPERATIONS.OP_INTELLIGENCE_READ);

// ─── Master Data modules ─────────────────────────────────────────────

const mdAdTypes = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_AD_TYPES_READ,
	APP_OPERATIONS.OP_MASTER_DATA_AD_TYPES_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_AD_TYPES_DELETE
);
export const isMasterDataAdTypesRead = mdAdTypes.isRead;
export const isMasterDataAdTypesWrite = mdAdTypes.isWrite;
export const isMasterDataAdTypesDelete = mdAdTypes.isDelete;
export const isMasterDataAdTypesReadWrite = mdAdTypes.isReadWrite;

const mdCustomers = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_CUSTOMERS_READ,
	APP_OPERATIONS.OP_MASTER_DATA_CUSTOMERS_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_CUSTOMERS_DELETE
);
export const isMasterDataCustomersRead = mdCustomers.isRead;
export const isMasterDataCustomersWrite = mdCustomers.isWrite;
export const isMasterDataCustomersDelete = mdCustomers.isDelete;
export const isMasterDataCustomersReadWrite = mdCustomers.isReadWrite;

const mdGroups = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_GROUPS_READ,
	APP_OPERATIONS.OP_MASTER_DATA_GROUPS_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_GROUPS_DELETE
);
export const isMasterDataGroupsRead = mdGroups.isRead;
export const isMasterDataGroupsWrite = mdGroups.isWrite;
export const isMasterDataGroupsDelete = mdGroups.isDelete;
export const isMasterDataGroupsReadWrite = mdGroups.isReadWrite;

const mdInventoryTransactions = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_INVENTORY_TRANSACTIONS_READ,
	APP_OPERATIONS.OP_MASTER_DATA_INVENTORY_TRANSACTIONS_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_INVENTORY_TRANSACTIONS_DELETE
);
export const isMasterDataInventoryTransactionsRead = mdInventoryTransactions.isRead;
export const isMasterDataInventoryTransactionsWrite = mdInventoryTransactions.isWrite;
export const isMasterDataInventoryTransactionsDelete = mdInventoryTransactions.isDelete;
export const isMasterDataInventoryTransactionsReadWrite = mdInventoryTransactions.isReadWrite;

const mdItems = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_ITEMS_READ,
	APP_OPERATIONS.OP_MASTER_DATA_ITEMS_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_ITEMS_DELETE
);
export const isMasterDataItemsRead = mdItems.isRead;
export const isMasterDataItemsWrite = mdItems.isWrite;
export const isMasterDataItemsDelete = mdItems.isDelete;
export const isMasterDataItemsReadWrite = mdItems.isReadWrite;

const mdItemExclusions = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_ITEM_EXCLUSIONS_READ,
	APP_OPERATIONS.OP_MASTER_DATA_ITEM_EXCLUSIONS_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_ITEM_EXCLUSIONS_DELETE
);
export const isMasterDataItemExclusionsRead = mdItemExclusions.isRead;
export const isMasterDataItemExclusionsWrite = mdItemExclusions.isWrite;
export const isMasterDataItemExclusionsDelete = mdItemExclusions.isDelete;
export const isMasterDataItemExclusionsReadWrite = mdItemExclusions.isReadWrite;

const mdItemShippingMethodConfig = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_ITEM_SHIPPING_METHOD_CONFIGURATION_READ,
	APP_OPERATIONS.OP_MASTER_DATA_ITEM_SHIPPING_METHOD_CONFIGURATION_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_ITEM_SHIPPING_METHOD_CONFIGURATION_DELETE
);
export const isMasterDataItemShippingMethodConfigurationRead = mdItemShippingMethodConfig.isRead;
export const isMasterDataItemShippingMethodConfigurationWrite = mdItemShippingMethodConfig.isWrite;
export const isMasterDataItemShippingMethodConfigurationDelete = mdItemShippingMethodConfig.isDelete;
export const isMasterDataItemShippingMethodConfigurationReadWrite = mdItemShippingMethodConfig.isReadWrite;

const mdKits = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_KITS_READ,
	APP_OPERATIONS.OP_MASTER_DATA_KITS_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_KITS_DELETE
);
export const isMasterDataKitsRead = mdKits.isRead;
export const isMasterDataKitsWrite = mdKits.isWrite;
export const isMasterDataKitsDelete = mdKits.isDelete;
export const isMasterDataKitsReadWrite = mdKits.isReadWrite;

const mdParameters = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_PARAMETERS_READ,
	APP_OPERATIONS.OP_MASTER_DATA_PARAMETERS_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_PARAMETERS_DELETE
);
export const isMasterDataParametersRead = mdParameters.isRead;
export const isMasterDataParametersWrite = mdParameters.isWrite;
export const isMasterDataParametersDelete = mdParameters.isDelete;
export const isMasterDataParametersReadWrite = mdParameters.isReadWrite;

const mdPricing = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_PRICING_READ,
	APP_OPERATIONS.OP_MASTER_DATA_PRICING_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_PRICING_DELETE
);
export const isMasterDataPricingRead = mdPricing.isRead;
export const isMasterDataPricingWrite = mdPricing.isWrite;
export const isMasterDataPricingDelete = mdPricing.isDelete;
export const isMasterDataPricingReadWrite = mdPricing.isReadWrite;

const mdPrimaryStoreSku = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_PRIMARY_STORE_SKU_READ,
	APP_OPERATIONS.OP_MASTER_DATA_PRIMARY_STORE_SKU_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_PRIMARY_STORE_SKU_DELETE
);
export const isMasterDataPrimaryStoreSkuRead = mdPrimaryStoreSku.isRead;
export const isMasterDataPrimaryStoreSkuWrite = mdPrimaryStoreSku.isWrite;
export const isMasterDataPrimaryStoreSkuDelete = mdPrimaryStoreSku.isDelete;
export const isMasterDataPrimaryStoreSkuReadWrite = mdPrimaryStoreSku.isReadWrite;

const mdSchedules = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_SCHEDULES_READ,
	APP_OPERATIONS.OP_MASTER_DATA_SCHEDULES_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_SCHEDULES_DELETE
);
export const isMasterDataSchedulesRead = mdSchedules.isRead;
export const isMasterDataSchedulesWrite = mdSchedules.isWrite;
export const isMasterDataSchedulesDelete = mdSchedules.isDelete;
export const isMasterDataSchedulesReadWrite = mdSchedules.isReadWrite;

const mdShipments = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_SHIPMENTS_READ,
	APP_OPERATIONS.OP_MASTER_DATA_SHIPMENTS_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_SHIPMENTS_DELETE
);
export const isMasterDataShipmentsRead = mdShipments.isRead;
export const isMasterDataShipmentsWrite = mdShipments.isWrite;
export const isMasterDataShipmentsDelete = mdShipments.isDelete;
export const isMasterDataShipmentsReadWrite = mdShipments.isReadWrite;

const mdShippingMethods = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_SHIPPING_METHODS_READ,
	APP_OPERATIONS.OP_MASTER_DATA_SHIPPING_METHODS_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_SHIPPING_METHODS_DELETE
);
export const isMasterDataShippingMethodsRead = mdShippingMethods.isRead;
export const isMasterDataShippingMethodsWrite = mdShippingMethods.isWrite;
export const isMasterDataShippingMethodsDelete = mdShippingMethods.isDelete;
export const isMasterDataShippingMethodsReadWrite = mdShippingMethods.isReadWrite;

const mdSkus = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_SKUS_READ,
	APP_OPERATIONS.OP_MASTER_DATA_SKUS_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_SKUS_DELETE
);
export const isMasterDataSkusRead = mdSkus.isRead;
export const isMasterDataSkusWrite = mdSkus.isWrite;
export const isMasterDataSkusDelete = mdSkus.isDelete;
export const isMasterDataSkusReadWrite = mdSkus.isReadWrite;

const mdSkuExclusions = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_SKU_EXCLUSIONS_READ,
	APP_OPERATIONS.OP_MASTER_DATA_SKU_EXCLUSIONS_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_SKU_EXCLUSIONS_DELETE
);
export const isMasterDataSkuExclusionsRead = mdSkuExclusions.isRead;
export const isMasterDataSkuExclusionsWrite = mdSkuExclusions.isWrite;
export const isMasterDataSkuExclusionsDelete = mdSkuExclusions.isDelete;
export const isMasterDataSkuExclusionsReadWrite = mdSkuExclusions.isReadWrite;

const mdStores = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_STORES_READ,
	APP_OPERATIONS.OP_MASTER_DATA_STORES_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_STORES_DELETE
);
export const isMasterDataStoresRead = mdStores.isRead;
export const isMasterDataStoresWrite = mdStores.isWrite;
export const isMasterDataStoresDelete = mdStores.isDelete;
export const isMasterDataStoresReadWrite = mdStores.isReadWrite;

const mdStoreOrderList = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_STORE_ORDER_LIST_READ,
	APP_OPERATIONS.OP_MASTER_DATA_STORE_ORDER_LIST_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_STORE_ORDER_LIST_DELETE
);
export const isMasterDataStoreOrderListRead = mdStoreOrderList.isRead;
export const isMasterDataStoreOrderListWrite = mdStoreOrderList.isWrite;
export const isMasterDataStoreOrderListDelete = mdStoreOrderList.isDelete;
export const isMasterDataStoreOrderListReadWrite = mdStoreOrderList.isReadWrite;

const mdValidSkuItemCombinations = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_VALID_SKU_ITEM_COMBINATIONS_READ,
	APP_OPERATIONS.OP_MASTER_DATA_VALID_SKU_ITEM_COMBINATIONS_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_VALID_SKU_ITEM_COMBINATIONS_DELETE
);
export const isMasterDataValidSkuItemCombinationsRead = mdValidSkuItemCombinations.isRead;
export const isMasterDataValidSkuItemCombinationsWrite = mdValidSkuItemCombinations.isWrite;
export const isMasterDataValidSkuItemCombinationsDelete = mdValidSkuItemCombinations.isDelete;
export const isMasterDataValidSkuItemCombinationsReadWrite = mdValidSkuItemCombinations.isReadWrite;

const mdValidStoreSkuCombinations = createAclSelectors(
	APP_OPERATIONS.OP_MASTER_DATA_VALID_STORE_SKU_COMBINATIONS_READ,
	APP_OPERATIONS.OP_MASTER_DATA_VALID_STORE_SKU_COMBINATIONS_WRITE,
	APP_OPERATIONS.OP_MASTER_DATA_VALID_STORE_SKU_COMBINATIONS_DELETE
);
export const isMasterDataValidStoreSkuCombinationsRead = mdValidStoreSkuCombinations.isRead;
export const isMasterDataValidStoreSkuCombinationsWrite = mdValidStoreSkuCombinations.isWrite;
export const isMasterDataValidStoreSkuCombinationsDelete = mdValidStoreSkuCombinations.isDelete;
export const isMasterDataValidStoreSkuCombinationsReadWrite = mdValidStoreSkuCombinations.isReadWrite;

// TODO: Previously hardcoded to `true` — now checks actual ACL permissions.
// Verify with product team that Warehouse access is correctly configured for all tenants.
const mdWarehouse = createAclSelectors(
	APP_OPERATIONS.OP_WAREHOUSE_READ,
	APP_OPERATIONS.OP_WAREHOUSE_WRITE,
	APP_OPERATIONS.OP_WAREHOUSE_DELETE
);
export const isMasterDataWarehouseRead = mdWarehouse.isRead;
export const isMasterDataWarehouseWrite = mdWarehouse.isWrite;
export const isMasterDataWarehouseDelete = mdWarehouse.isDelete;
export const isMasterDataWarehouseReadWrite = mdWarehouse.isReadWrite;

// ─── App Settings ────────────────────────────────────────────────────

export const isAppSettingsRead = (state: RootState): boolean =>
	findAccessOperation(state, APP_OPERATIONS.OP_APP_SETTINGS_READ);
export const isAppSettingWrite = (state: RootState): boolean =>
	findAccessOperation(state, APP_OPERATIONS.OP_APP_SETTINGS_WRITE);
export const isAppSettingDelete = (state: RootState): boolean =>
	findAccessOperation(state, APP_OPERATIONS.OP_APP_SETTINGS_DELETE);

// ─── ACL-guarded modules (require hasAclOperations) ──────────────────

const aclRoles = createAclSelectors(APP_OPERATIONS.OP_ACL_ROLES_READ, APP_OPERATIONS.OP_ACL_ROLES_WRITE, APP_OPERATIONS.OP_ACL_ROLES_WRITE, { requireAcl: true });
// Note: isAclRolesReadEnabled uses isAccessControlEnabled directly (stricter than hasAclOperations)
export const isAclRolesReadEnabled = (state: RootState): boolean =>
	Boolean(isAccessControlEnabled(state)) && findAccessOperation(state, APP_OPERATIONS.OP_ACL_ROLES_READ);
export const isAclRolesWriteEnabled = aclRoles.isWrite;
export const isAclRolesReadWriteEnabled = (state: RootState): boolean =>
	isAclRolesReadEnabled(state) || isAclRolesWriteEnabled(state);

const aclUsers = createAclSelectors(APP_OPERATIONS.OP_ACL_USERS_READ, APP_OPERATIONS.OP_ACL_USERS_WRITE, APP_OPERATIONS.OP_ACL_USERS_WRITE, { requireAcl: true });
export const isAclUsersReadEnabled = aclUsers.isRead;
export const isAclUsersWriteEnabled = aclUsers.isWrite;
export const isAclUsersReadWriteEnabled = aclUsers.isReadWrite;

const columnAlias = createAclSelectors(APP_OPERATIONS.OP_APP_COLUMN_ALIAS_READ, APP_OPERATIONS.OP_APP_COLUMN_ALIAS_WRITE, APP_OPERATIONS.OP_APP_COLUMN_ALIAS_DELETE, { requireAcl: true });
export const isAppAliasSettingRead = columnAlias.isRead;
export const isAppAliasSettingWrite = columnAlias.isWrite;
export const isAppAliasSettingDelete = columnAlias.isDelete;
export const isAppAliasSettingReadWrite = (state: RootState): boolean =>
	isAppSettingsRead(state) || isAppAliasSettingWrite(state);

const aclDataRestrictions = createAclSelectors(APP_OPERATIONS.OP_ACL_DATA_RESTRICTIONS_READ, APP_OPERATIONS.OP_ACL_DATA_RESTRICTIONS_WRITE, APP_OPERATIONS.OP_ACL_DATA_RESTRICTIONS_WRITE, { requireAcl: true });
export const isAclDataRestrictionsReadEnabled = aclDataRestrictions.isRead;
export const isAclDataRestrictionsWriteEnabled = aclDataRestrictions.isWrite;
export const isAclDataRestrictionsReadWriteEnabled = aclDataRestrictions.isReadWrite;

const planogramRestrictions = createAclSelectors(APP_OPERATIONS.OP_PLANOGRAM_READ, APP_OPERATIONS.OP_PLANOGRAM_WRITE, APP_OPERATIONS.OP_PLANOGRAM_DELETE, { requireAcl: true });
export const isPlanogramRestrictionsReadEnabled = planogramRestrictions.isRead;
export const isPlanogramRestrictionsWriteEnabled = planogramRestrictions.isWrite;
export const isPlanogramRestrictionsReadWriteEnabled = planogramRestrictions.isReadWrite;

const presentationQuantity = createAclSelectors(APP_OPERATIONS.OP_PRESENTATION_QUANTITY_READ, APP_OPERATIONS.OP_PRESENTATION_QUANTITY_WRITE, APP_OPERATIONS.OP_PRESENTATION_QUANTITY_DELETE, { requireAcl: true });
export const isMasterDataPresentationQuantityReadEnabled = presentationQuantity.isRead;
export const isMasterDataPresentationQuantityWriteEnabled = presentationQuantity.isWrite;
export const isMasterDataPresentationQuantityDeletgeEnabled = presentationQuantity.isDelete;
export const isMasterDataPresentationQuantityReadWriteEnabled = presentationQuantity.isReadWrite;

const retailSales = createAclSelectors(APP_OPERATIONS.OP_RETAIL_SALES_READ, APP_OPERATIONS.OP_RETAIL_SALES_WRITE, APP_OPERATIONS.OP_RETAIL_SALES_DELETE, { requireAcl: true });
export const isMasterDataRetailSalesReadEnabled = retailSales.isRead;
export const isMasterDataRetailSalesWriteEnabled = retailSales.isWrite;
export const isMasterDataRetailSalesDeleteEnabled = retailSales.isDelete;
export const isMasterDataRetailSalesReadWriteEnabled = retailSales.isReadWrite;

// ─── Composite selectors ─────────────────────────────────────────────

export const isAppSettingEnabled = (state: RootState): boolean =>
	isAppAliasSettingReadWrite(state) || isAppAliasSettingDelete(state);

export const isMasterDataRead = (state: RootState): boolean =>
	isMasterDataAdTypesReadWrite(state) ||
	isMasterDataCustomersReadWrite(state) ||
	isMasterDataGroupsReadWrite(state) ||
	isMasterDataInventoryTransactionsReadWrite(state) ||
	isMasterDataItemsReadWrite(state) ||
	isMasterDataItemExclusionsReadWrite(state) ||
	isMasterDataItemShippingMethodConfigurationReadWrite(state) ||
	isMasterDataKitsReadWrite(state) ||
	isMasterDataParametersReadWrite(state) ||
	isMasterDataPricingReadWrite(state) ||
	isMasterDataPrimaryStoreSkuReadWrite(state) ||
	isMasterDataSchedulesReadWrite(state) ||
	isMasterDataShipmentsReadWrite(state) ||
	isMasterDataShippingMethodsReadWrite(state) ||
	isMasterDataSkusReadWrite(state) ||
	isMasterDataSkuExclusionsReadWrite(state) ||
	isMasterDataStoresReadWrite(state) ||
	isMasterDataStoreOrderListReadWrite(state) ||
	isMasterDataValidSkuItemCombinationsReadWrite(state) ||
	isMasterDataValidStoreSkuCombinationsReadWrite(state) ||
	isMasterDataPresentationQuantityReadWriteEnabled(state) ||
	isMasterDataRetailSalesReadWriteEnabled(state) ||
	isMasterDataWarehouseReadWrite(state);

// ─── Feature flags (ACL-guarded single selectors) ────────────────────

export const isRaUpload = (state: RootState): boolean =>
	hasAclOperations(state) && findAccessOperation(state, APP_OPERATIONS.OP_RA_UPLOAD_READ);
export const isRaUploadWrite = (state: RootState): boolean =>
	hasAclOperations(state) && findAccessOperation(state, APP_OPERATIONS.OP_RA_UPLOAD_WRITE);
export const isRaUploadDelete = (state: RootState): boolean =>
	hasAclOperations(state) && findAccessOperation(state, APP_OPERATIONS.OP_RA_UPLOAD_DELETE);
export const isRaUploadReadWriteDelete = (state: RootState): boolean =>
	isRaUpload(state) || isRaUploadWrite(state);

export const isForecast = (state: RootState): boolean =>
	hasAclOperations(state) && findAccessOperation(state, APP_OPERATIONS.OP_LOWES_FORECAST_READ);
export const isReplenishmentRead = (state: RootState): boolean =>
	hasAclOperations(state) && findAccessOperation(state, APP_OPERATIONS.OP_ORDER_WRITING_READ);
export const isMatrix = (state: RootState): boolean =>
	hasAclOperations(state) && findAccessOperation(state, APP_OPERATIONS.OP_MATRIX_READ);
export const isAimRead = (state: RootState): boolean =>
	hasAclOperations(state) && findAccessOperation(state, APP_OPERATIONS.OP_AIM_READ);
export const isReportRead = (state: RootState): boolean =>
	hasAclOperations(state) && findAccessOperation(state, APP_OPERATIONS.OP_REPORTING_READ);
